import React from 'react';
import PropTypes from 'prop-types';
import { getClassName } from '../../../utils/ui/classes';
import { Button } from '@cargomatic/ui';

const sizes = {
  xsmall: 'xsmall',
  small: 'small',
  normal: 'normal',
};

function getColorClass({ color, disabled, active }) {
  if (disabled) {
    return ['text-muted-foreground/50'];
  }

  const ringClass = {
    'ring-2': active,
    'ring-offset-2': active,
  };

  switch (color) {
    case 'blue':
      return ['text-blue-400', 'hover:bg-blue-50', ringClass];
    case 'red':
      return ['text-red-600', 'hover:bg-red-50', ringClass];
    case 'yellow':
      return ['text-yellow-600', 'hover:bg-yellow-50', ringClass];
    case 'brand':
      return [
        {
          'text-white': active,
          'bg-green-600': active,
          'text-green-600': !active,
        },
        'hover:bg-green-50',
        ringClass,
      ];
    case 'green':
      return [
        {
          'text-white': active,
          'bg-green-600': active,
          'text-green-600': !active,
        },
        'hover:bg-green-50',
        ringClass,
      ];
    default:
      return ['text-gray-400', 'hover:bg-gray-50', ringClass];
  }
}

function getSizeClass(size = sizes.normal) {
  switch (size) {
    case sizes.xsmall:
      return ['w-5', 'h-5'];
    case sizes.small:
      return ['w-8', 'h-8'];
    default:
      return ['w-9', 'h-9'];
  }
}

export function getCircleButtonClass({
  color,
  disabled,
  size = sizes.normal,
  className,
  active,
}) {
  return getClassName(
    {
      'rounded-full': true,
      'flex-grow-0': true,
      'flex-shrink-0': true,
      'pointer-events-none': disabled,
      'pointer-events-auto': !disabled,
      flex: true,
      'items-center': true,
      'justify-center': true,
    },
    ...getColorClass({ color, disabled, active }),
    ...getSizeClass(size),
    className,
  );
}
const CircleButton = ({
  color,
  title,
  size,
  id,
  className,
  disabled,
  children,
  onClick,
  active,
  dataTestId,
}) => {
  function handleClick(e) {
    e.preventDefault();
    onClick(e);
  }

  return (
    <Button
      id={id}
      size="icon"
      variant="ghost"
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      data-testid={dataTestId || 'circleButton'}
      className={getCircleButtonClass({
        color,
        size,
        className,
        disabled,
        active,
      })}
    >
      {children}
    </Button>
  );
};

CircleButton.propTypes = {
  color: PropTypes.string.isRequired,
  size: PropTypes.oneOf(Object.keys(sizes)),
  title: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.any,
  id: PropTypes.string,
  active: PropTypes.bool,
  dataTestId: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

CircleButton.defaultProps = {
  size: sizes.normal,
  title: '',
  disabled: false,
  active: false,
};

export default CircleButton;
